import {
	Box,
	Checkbox,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type React from "react";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { useProductCategoriesQuery } from "AdvisoryManagement/Api/Queries/useProductCategoriesQuery";

export type DocumentItem = {
	categoryCode: string | null | undefined;
	documentId: string;
	documentType: string;
	dmsDocumentId: string;
};

interface DocumentSelectionTableProps {
	documents: DocumentItem[];
	onConfirm: (selected: DocumentItem[]) => void;
	defaultSelectedIds?: string[];
	confirmLabel?: string;
}

type FormValues = {
	selected: Record<string, boolean>;
};

export interface DocumentSelectionTableRef {
	submit: () => void;
}

export const DocumentSelectionTable = forwardRef<
	DocumentSelectionTableRef,
	DocumentSelectionTableProps
>(({ documents, onConfirm, defaultSelectedIds = [] }, ref) => {
	const productCategories = useProductCategoriesQuery();

	const defaultSelected = useMemo(() => {
		const map: Record<string, boolean> = {};
		for (const d of documents)
			map[d.documentId] = defaultSelectedIds.includes(d.documentId);
		return map;
	}, [documents, defaultSelectedIds]);

	const { control, handleSubmit, setValue } = useForm<FormValues>({
		defaultValues: { selected: defaultSelected },
		mode: "onChange",
	});

	const fieldNames = useMemo(
		() => documents.map((d) => `selected.${d.documentId}`),
		[documents],
	);
	const selectedList = useWatch({ control, name: fieldNames as any }) as
		| (boolean | undefined)[]
		| undefined;
	const total = documents.length;
	const checkedCount = (selectedList ?? []).filter(Boolean).length;
	const allChecked = total > 0 && checkedCount === total;
	const isIndeterminate = checkedCount > 0 && checkedCount < total;

	const onToggleAll = (
		_: React.ChangeEvent<HTMLInputElement>,
		checked: boolean,
	) => {
		const updated: Record<string, boolean> = {};
		for (const d of documents) updated[d.documentId] = checked;
		setValue("selected", updated, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		});
	};

	const onSubmit = (values: FormValues) => {
		const result = documents.filter((d) => values.selected?.[d.documentId]);
		onConfirm(result);
	};

	useImperativeHandle(ref, () => ({
		submit: handleSubmit(onSubmit),
	}));

	return (
		<Paper sx={{ p: 0, mt: 2 }}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ mb: 1 }}
			>
				<Typography variant="h5">Vyberte připravené dokumenty</Typography>
				<Typography variant="body2" color="text.secondary">
					vybráno {checkedCount}/{total}
				</Typography>
			</Stack>

			<Box component="form" onSubmit={handleSubmit(onSubmit)}>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox">
									<Checkbox
										checked={allChecked}
										indeterminate={isIndeterminate}
										onChange={onToggleAll}
									/>
								</TableCell>
								<TableCell>Typ produktu</TableCell>
								<TableCell>Typ dokumentu</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{documents.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<Typography variant="body2" color="text.secondary">
											Nejsou k dispozici žádné dokumenty pro vytvoření průvodky.
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								documents.map((doc) => (
									<TableRow key={doc.documentId} hover>
										<TableCell padding="checkbox">
											<Controller
												name={`selected.${doc.documentId}` as const}
												control={control}
												render={({ field }) => (
													<Checkbox {...field} checked={Boolean(field.value)} />
												)}
											/>
										</TableCell>
										<TableCell>
											{productCategories.data?.find(
												(c) => c.code === doc.categoryCode,
											)?.name || doc.categoryCode}
										</TableCell>
										<TableCell>{doc.documentType}</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Paper>
	);
});
